import { Button, Flex, Progress, Tooltip } from "@mantine/core";
import {
  CommentActions,
  FlatHierarchy,
  IEditorMetadata,
  IMinProject,
  IPNSProps,
  IUserDepricated,
  onIssueChange,
  TodoActions,
} from "@mctypes/global.types";
import { IAccessPopulated } from "@schemas/accessSchema";
import { IIssueBoardApi } from "@schemas/issueBoardSchema";
import { IIssueApi } from "@schemas/issueSchema";
import { IProjectMemberApiItem } from "@schemas/projectSchema";
import { IconCopy, IconExternalLink } from "@tabler/icons-react";
import React, { useMemo, useState } from "react";
import { useHistory } from "react-router";
import { Checkbox, FoldableSectionDeprecated, McInput, TodoList } from "..";
import { formatShortDate } from "../../app/routes/admin/selectors";
import MultiCommentMantine from "../../app/routes/issuesApp/IssuesTab/IssueItemModal/new/MultiCommentMantine";
import { pathIsInIssueApp } from "../../app/routes/issuesApp/issueUtils";
import { AccessLevelsEnum, IssueDisabledColumnsEnum } from "../../mc-shared/enums";
import { AppModalEnums } from "../../mc-shared/sharedConstants";
import LocalErrorBoundary from "../../util/LocalErrorBoundary";
import CustomAttributeSection from "../CustomAttributes/CustomAttributeSection";
import { IssueConnectedIssuesSection } from "./IssueConnectedIssuesSection";
import IssueConnectionsSection from "./IssueConnectionsSection";
import { ConnectIssueModal } from "./IssueConnectIssueModal";
import "./IssueDetails.css";
import IssueDetailsSection from "./IssueDetailsSection";
import IssueGantActivites from "./IssueGantActivites";
import IssueRisikoSection from "./IssueRisikoSection";
import IssueTimeSection from "./IssueTimeSection";
import IssueUserSection from "./IssueUserSection";

const IssueDetails: React.FC<{
  issue: IIssueApi;
  board: IIssueBoardApi;
  members: IProjectMemberApiItem[];
  pnsItems: FlatHierarchy<IPNSProps>;
  onValueChange: onIssueChange;
  commentChange: (id: string, action: CommentActions, value?: string, metadata?: IEditorMetadata) => void;
  todoChange: (id: string, action: TodoActions, value?: string, isChecked?: boolean) => Promise<void>;
  onTagsChange: (oldValue: string[], newValue: string[]) => void;
  tagOptions: string[];
  loggedInUser: IUserDepricated;
  onOpenConnectionModal: (issueId: string, issueBoardId: string, app: AppModalEnums) => void;
}> = ({
  issue,
  board,
  members,
  onValueChange,
  commentChange,
  todoChange,
  onTagsChange,
  pnsItems,
  tagOptions,
  loggedInUser,
  onOpenConnectionModal,
}) => {
  const history = useHistory();
  const [connectIssueModalOpened, setConnectIssueModalOpened] = useState<boolean>(false);
  const { showConclusion, buckets, types, access } = board ?? {};

  const accessUsers: IProjectMemberApiItem[] = useMemo(() => {
    if (access == null) {
      return [];
    }

    if (access.level === AccessLevelsEnum.USERS) {
      return (access as IAccessPopulated).users;
    } else if (access.level === AccessLevelsEnum.GROUPS) {
      return members
        .filter((member) => access.groups.includes(member.group) || member.group === "ADMIN")
        .map((member) => member.user);
    }

    return [];
  }, [access, members]);

  const project = issue.project as IMinProject;
  const projectId = project?._id || (project as string);
  const issueBoardId = issue?.issueBoard?._id;

  const datesDisabled = board?.disabledColumns?.includes(IssueDisabledColumnsEnum.DATESSECTION);

  const hoursColumns = [
    IssueDisabledColumnsEnum.HOURSORIGINALESTIMATE,
    IssueDisabledColumnsEnum.HOURSCOMPLETED,
    IssueDisabledColumnsEnum.HOURSREMAINING,
  ];

  const hoursDisabled = hoursColumns.every((col) => board?.disabledColumns?.includes(col));

  const showTimeSection = !(datesDisabled && hoursDisabled);

  return (
    <div className="d-flex flex-column pr-4 pl-4">
      <FoldableSectionDeprecated>
        <FoldableSectionDeprecated.Header>
          <div className="d-flex flex-column">
            <div className="section-title mr-4">
              <McInput
                onBlur={(e) => onValueChange(issue.title, e.target.value, "title")}
                value={issue.title}
                placeholder="Skriv inn tittel"
                style={{ fontSize: "1.2rem" }}
              />
            </div>
            <div className="d-flex justify-content-between mr-4">
              {typeof issue.lastUpdatedBy !== "string" && issue.lastUpdatedBy?.name == null && (
                <div className="status-text truncateHeaderText gray-600-text">
                  Opprettet av {typeof issue.createdBy !== "string" && issue.createdBy?.name},{" "}
                  {formatShortDate(issue.createdAt)}
                </div>
              )}
              {typeof issue.lastUpdatedBy !== "string" &&
                issue.lastUpdatedBy?.name != null &&
                issue.status !== "Fullført" && (
                  <div className="status-text truncateHeaderText gray-600-text">
                    Oppdatert av {typeof issue.lastUpdatedBy !== "string" && issue.lastUpdatedBy?.name},{" "}
                    {formatShortDate(issue.updatedAt)}
                  </div>
                )}
              {issue.status === "Fullført" && (
                <div className="status-text truncateHeaderText text-success">
                  Fullført {formatShortDate(issue?.dates?.completed)}
                </div>
              )}
              {(issueBoardId == null || pathIsInIssueApp(history.location.pathname) === false) && (
                <Flex gap={0} align="center">
                  <Tooltip zIndex={10000} label={`Gå til ${board?.name}`} withArrow position="bottom">
                    <a
                      style={{ color: "var(--mantine-color-mc-primary-6)" }}
                      href={`/#/project/${projectId}/issues/${board?._id}`}
                      className="status-text"
                    >
                      <Flex align="center" gap={0}>
                        {board?.name}
                        <IconExternalLink size={14} />
                      </Flex>
                    </a>
                  </Tooltip>

                  <Tooltip zIndex={10000} label="Kopier lenke" withArrow position="bottom">
                    <Button
                      variant="subtle"
                      size="xs"
                      onClick={() =>
                        navigator.clipboard.writeText(
                          `${window.location.origin}/#/project/${projectId}/issues/${board?._id}`,
                        )
                      }
                    >
                      <IconCopy size={14} />
                    </Button>
                  </Tooltip>
                </Flex>
              )}
            </div>
          </div>
        </FoldableSectionDeprecated.Header>
        <FoldableSectionDeprecated.Body>
          <div className="d-flex flex-column w-100 mt-2 mb-2">
            <LocalErrorBoundary id="IssueUserSection">
              <IssueUserSection
                issue={issue}
                projectMembers={members.map((m) => m.user as IUserDepricated).filter((u) => u != null)}
                onChange={onValueChange}
                accessUsers={accessUsers?.filter((au) => members.some((m) => m.user?._id === au?._id)) ?? []}
                accessLevel={access?.level}
                disabledColumns={board.disabledColumns}
              />
            </LocalErrorBoundary>
            <LocalErrorBoundary id="IssueDetailsSection">
              <IssueDetailsSection
                issue={issue}
                showConclusion={showConclusion ?? false}
                boardBuckets={buckets ?? []}
                boardTypes={types ?? []}
                onChange={onValueChange}
                onTagChange={(oldValue, newValue) => onTagsChange(oldValue, newValue)}
                tagOptions={tagOptions}
                allowNewTags={board.useOnlyPredefinedTags !== true}
                pnsItems={pnsItems}
                disabledColumns={board.disabledColumns}
              />
            </LocalErrorBoundary>
          </div>
        </FoldableSectionDeprecated.Body>
      </FoldableSectionDeprecated>
      {board.risikoSection?.isActive && issue.taskOrIssue === "ISSUE" && (
        <FoldableSectionDeprecated>
          <FoldableSectionDeprecated.Header>
            <div className="section-title">Risiko</div>
          </FoldableSectionDeprecated.Header>
          <FoldableSectionDeprecated.Body>
            <div className="d-flex flex-column w-100 mt-2 mb-2">
              <LocalErrorBoundary id="IssueRisikoSection">
                <IssueRisikoSection issue={issue} issueBoard={board} onChange={onValueChange} />
              </LocalErrorBoundary>
            </div>
          </FoldableSectionDeprecated.Body>
        </FoldableSectionDeprecated>
      )}
      {showTimeSection && (
        <FoldableSectionDeprecated>
          <FoldableSectionDeprecated.Header>
            <div className="section-title">Tidsplan</div>
          </FoldableSectionDeprecated.Header>
          <FoldableSectionDeprecated.Body>
            <div className="d-flex flex-column w-100 mt-2 mb-2">
              <IssueTimeSection issue={issue} onChange={onValueChange} disabledColumns={board.disabledColumns} />
            </div>
          </FoldableSectionDeprecated.Body>
        </FoldableSectionDeprecated>
      )}
      {issue.taskOrIssue === "ISSUE" && Object.keys(board?.otherAttributesConfig ?? {}).length > 0 && (
        <LocalErrorBoundary id="CustomAttributeSection">
          <CustomAttributeSection
            issueLocalId={issue.id}
            attributeConfig={board?.otherAttributesConfig}
            data={issue.otherAttributesData ?? {}}
            onSave={(oldValue, newValue, attributeName) => {
              if (oldValue !== newValue) {
                onValueChange(
                  oldValue?.toString() ?? "",
                  newValue,
                  `otherAttributesData.${attributeName}` as keyof IIssueApi,
                );
              }
            }}
          />
        </LocalErrorBoundary>
      )}
      {issue.taskOrIssue === "TASK" && Object.keys(board?.otherAttributesTasksConfig ?? {}).length > 0 && (
        <LocalErrorBoundary id="CustomAttributeSection">
          <CustomAttributeSection
            issueLocalId={issue.id}
            attributeConfig={board?.otherAttributesTasksConfig}
            data={issue.otherAttributesData ?? {}}
            onSave={(oldValue, newValue, attributeName) => {
              if (oldValue !== newValue) {
                onValueChange(
                  oldValue?.toString() ?? "",
                  newValue,
                  `otherAttributesData.${attributeName}` as keyof IIssueApi,
                );
              }
            }}
          />
        </LocalErrorBoundary>
      )}
      <FoldableSectionDeprecated>
        <FoldableSectionDeprecated.Header>
          <div className="d-flex">
            <span className="section-title">Sjekkliste</span>
            <div className="flex-fill" />
            <Checkbox
              color="#ccc"
              label="Vis i kanban"
              isChecked={issue.showTodosInKanban === true}
              setChecked={() => onValueChange(issue.showTodosInKanban, !issue.showTodosInKanban, "showTodosInKanban")}
            />
            <div className="ml-4" />
          </div>
          <div className="section-title d-flex align-items-center">
            {issue.todos?.length > 0 && (
              <>
                <div className="section-title-progress">
                  {issue.todos.filter((todo) => todo.completedByName != null).length}/{issue.todos.length}
                </div>
                <Progress
                  className="w-100 mr-4"
                  value={(issue.todos.filter((todo) => todo.completedByName != null).length / issue.todos.length) * 100}
                  size="xs"
                />
              </>
            )}
          </div>
        </FoldableSectionDeprecated.Header>
        <FoldableSectionDeprecated.Body>
          <div className="d-flex flex-column w-100 mt-2 mb-2 mr-4">
            <LocalErrorBoundary id="TodoList">
              <TodoList
                todos={issue.todos}
                addNewTodo={async (text) => await todoChange(null, "ADD", text)}
                updateTodo={async (_id, text) => await todoChange(_id, "UPDATE", text)}
                deleteTodo={async (_id) => await todoChange(_id, "DELETE")}
                toggleTodo={async (_id, isChecked) => await todoChange(_id, "TOGGLE", null, isChecked)}
              />
            </LocalErrorBoundary>
          </div>
        </FoldableSectionDeprecated.Body>
      </FoldableSectionDeprecated>
      {import.meta.env.DEV === true && (
        <FoldableSectionDeprecated>
          <FoldableSectionDeprecated.Header>
            <div className="d-flex">
              <span className="section-title">Gantt-aktiviteter</span>
            </div>
          </FoldableSectionDeprecated.Header>
          <FoldableSectionDeprecated.Body>
            <IssueGantActivites issue={issue} />
          </FoldableSectionDeprecated.Body>
        </FoldableSectionDeprecated>
      )}
      {(issue.connectedChecklistItems?.length > 0 || issue.connectedMeetingAgendas?.length > 0) && (
        <FoldableSectionDeprecated>
          <FoldableSectionDeprecated.Header>
            <div className="section-title">Tilkoblinger</div>
          </FoldableSectionDeprecated.Header>
          <FoldableSectionDeprecated.Body>
            <div className="d-flex flex-column w-100 mt-2 mb-2 mr-4">
              <LocalErrorBoundary id="IssueConnectionsSection">
                <IssueConnectionsSection
                  projectId={projectId}
                  connectedMeetingAgendas={issue.connectedMeetingAgendas}
                  connectedChecklistItems={issue.connectedChecklistItems}
                  onOpenConnectionModal={onOpenConnectionModal}
                />
              </LocalErrorBoundary>
            </div>
          </FoldableSectionDeprecated.Body>
        </FoldableSectionDeprecated>
      )}
      <FoldableSectionDeprecated>
        <FoldableSectionDeprecated.Header>
          <div className="section-title">Diskusjon</div>
        </FoldableSectionDeprecated.Header>
        <FoldableSectionDeprecated.Body>
          <LocalErrorBoundary id="IssueCommentSection">
            <div className="d-flex flex-column w-100 mt-2 mb-2 mr-4">
              <MultiCommentMantine
                isLoading={false}
                showCancelButton={false}
                comments={issue.comments}
                onAdd={(text, metadata) => commentChange(null, "ADD", text, metadata)}
                onDelete={(_id) => commentChange(_id, "DELETE")}
                onUpdate={(_id, text, metadata) => commentChange(_id, "UPDATE", text, metadata)}
                loggedInUser={loggedInUser}
                members={members}
              />
            </div>
          </LocalErrorBoundary>
        </FoldableSectionDeprecated.Body>
      </FoldableSectionDeprecated>
      <FoldableSectionDeprecated>
        <FoldableSectionDeprecated.Header>
          <div className="d-flex">
            <div className="section-title">Tilkoblinger</div>
            <ConnectIssueModal
              projectId={projectId}
              boardId={issueBoardId}
              sourceIssueId={issue._id}
              opened={connectIssueModalOpened}
              onClose={() => setConnectIssueModalOpened(false)}
            />
            <div className="ml-4" />
          </div>
        </FoldableSectionDeprecated.Header>
        <FoldableSectionDeprecated.Body>
          <LocalErrorBoundary id="IssueConnectionsSection">
            <div className="d-flex flex-column w-100 mt-2 mb-2 mr-4">
              <IssueConnectedIssuesSection issue={issue} boardId={issueBoardId} projectId={projectId} />
            </div>
          </LocalErrorBoundary>
        </FoldableSectionDeprecated.Body>
      </FoldableSectionDeprecated>
    </div>
  );
};

export default IssueDetails;
